import React from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import NotificationsBell from "@/components/layout/NotificationsBell";
import { Utensils, Calendar, CreditCard, Bell, LogOut, ChefHat, ClipboardList, LayoutDashboard, Store } from "lucide-react";

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const isStaff = user?.role === "staff";
    const isKitchen = user?.role === "kitchen";
    const isAdmin = user?.role === "admin";

    const customerNav = [
        { to: "/", label: "Discover", icon: Utensils },
        { to: "/reservations", label: "Reservations", icon: Calendar },
        { to: "/cards", label: "Cards", icon: CreditCard },
    ];
    const staffNav = [
        { to: "/staff", label: "Overview", icon: LayoutDashboard },
        { to: "/staff/host", label: "Host", icon: ClipboardList },
    ];
    const kitchenNav = [
        { to: "/kitchen", label: "Kitchen", icon: ChefHat },
    ];
    const adminNav = [
        { to: "/admin", label: "Overview", icon: LayoutDashboard },
        { to: "/admin/restaurants", label: "Restaurants", icon: Store },
    ];

    let nav = customerNav;
    if (location.pathname.startsWith("/staff")) nav = staffNav;
    else if (location.pathname.startsWith("/kitchen")) nav = kitchenNav;
    else if (location.pathname.startsWith("/admin")) nav = adminNav;
    else if (isStaff) nav = staffNav;
    else if (isKitchen) nav = kitchenNav;
    else if (isAdmin) nav = adminNav;

    const brandHome =
        isStaff ? "/staff" :
        isKitchen ? "/kitchen" :
        isAdmin ? "/admin" :
        "/";

    const initials = (user?.name || "U").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

    return (
        <div className="min-h-screen flex flex-col bg-background">
            <header className="sticky top-0 z-30 border-b border-border/70 bg-card/85 backdrop-blur" data-testid="app-header">
                <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-3 flex items-center gap-3">
                    <Link to={brandHome} className="flex items-center gap-2 mr-2" data-testid="brand-link">
                        <div className="h-9 w-9 rounded-xl bg-[hsl(var(--brand-terracotta))] text-[hsl(var(--primary-foreground))] grid place-items-center shadow-sm">
                            <Utensils className="h-5 w-5" />
                        </div>
                        <div className="leading-tight">
                            <div className="font-display text-xl">ReserveLite</div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Seamless Dining</div>
                        </div>
                    </Link>
                    <nav className="hidden md:flex items-center gap-1 ml-4">
                        {nav.map(n => {
                            const active = location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to));
                            const Icon = n.icon;
                            return (
                                <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                                        active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-accent/40"
                                    }`}>
                                    <Icon className="h-4 w-4" /> {n.label}
                                </Link>
                            );
                        })}
                    </nav>
                    <div className="ml-auto flex items-center gap-2">
                        {user?.role && user.role !== "customer" && (
                            <Badge variant="secondary" className="hidden sm:inline-flex capitalize" data-testid="role-badge">{user.role}</Badge>
                        )}
                        <NotificationsBell />
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button className="flex items-center gap-2 p-1 rounded-full hover:bg-accent/40" data-testid="user-menu-trigger">
                                    <Avatar className="h-8 w-8">
                                        <AvatarImage src={user?.picture} alt={user?.name} />
                                        <AvatarFallback>{initials}</AvatarFallback>
                                    </Avatar>
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                                <DropdownMenuLabel>
                                    <div className="font-medium truncate">{user?.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {user?.role === "customer" && (
                                    <>
                                        <DropdownMenuItem onClick={() => navigate("/reservations")} data-testid="menu-reservations">
                                            <Calendar className="h-4 w-4 mr-2" /> My Reservations
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => navigate("/cards")} data-testid="menu-cards">
                                            <CreditCard className="h-4 w-4 mr-2" /> Payment Cards
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => navigate("/notifications")} data-testid="menu-notifications">
                                            <Bell className="h-4 w-4 mr-2" /> Notifications
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="menu-logout">
                                    <LogOut className="h-4 w-4 mr-2" /> Sign out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
                {/* Mobile nav */}
                <div className="md:hidden border-t border-border/70 px-2 py-2 flex gap-1 overflow-x-auto">
                    {nav.map(n => {
                        const active = location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to));
                        const Icon = n.icon;
                        return (
                            <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs whitespace-nowrap ${
                                    active ? "bg-secondary text-secondary-foreground" : "text-muted-foreground"
                                }`}>
                                <Icon className="h-3.5 w-3.5" /> {n.label}
                            </Link>
                        );
                    })}
                </div>
            </header>
            <main className="flex-1" data-testid="app-main">
                <Outlet />
            </main>
            <footer className="border-t border-border/70 mt-10">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 flex items-center justify-between text-xs text-muted-foreground">
                    <div>© ReserveLite · Seamless Dining Experience</div>
                    <div className="hidden sm:block">Crafted for hospitality</div>
                </div>
            </footer>
        </div>
    );
}
